import { useCallback, useEffect, useRef, useState } from "react";

// ─── How long a headline waits for its Hebrew (bug round, 17/10/2026) ────────
// A headline used to be drawn in English the moment the article arrived and
// swapped for the Hebrew one a second later · a flash of English on every
// open. Now a row whose translation is on its way holds a placeholder instead,
// for at most TRANSLATION_WAIT_MS. After that the original is shown (with the
// Headline note that says why), so a slow or failed translation never leaves
// a grey block where the article should be.
export const TRANSLATION_WAIT_MS = 6000;

// `keys`: the rows whose clocks should run now · the whole list, or only the
// rows on screen when translation starts on scroll (NewsTab). A row's clock
// starts the first time its key appears here and never restarts.
// Returns waiting(key): true until that row's clock has run out. A key whose
// clock has not started yet is waiting too, so the first paint shows the
// placeholder rather than one frame of English.
export function useTranslationWait(keys, ms = TRANSLATION_WAIT_MS) {
  const started = useRef(new Map());
  const timers = useRef(new Set());
  const [, tick] = useState(0);
  const sig = keys.filter(Boolean).join("\u0001");

  useEffect(() => {
    const now = Date.now();
    for (const k of sig ? sig.split("\u0001") : []) {
      if (started.current.has(k)) continue;
      started.current.set(k, now);
      const t = setTimeout(() => { timers.current.delete(t); tick(n => n + 1); }, ms);
      timers.current.add(t);
    }
  }, [sig, ms]);

  // Timers are cleared only when the screen goes away · not when the key list
  // changes, or a row whose timer was cleared would wait forever.
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);

  return useCallback(key => {
    // A row with no key cannot have a clock: it is not held back.
    if (!key) return false;
    const t = started.current.get(key);
    return t == null || Date.now() - t < ms;
  }, [ms]);
}
